import { useState } from 'react';
import { extractDateString } from '../lib/date-utils.js';
import type { KpiTeam } from '../types/api.js';

/**
 * KPI詳細ドリルダウンモーダルの状態
 * periodType: '90d' = 過去90日間、'mtd' = 月初から当該日まで
 * chartType: クリックされたチャートの種類（タブのデフォルト選択に使用）
 */
interface KpiDetailModalState {
  /** モーダル表示状態 */
  open: boolean;
  /** 対象チーム */
  team: KpiTeam;
  /** チーム表示名 */
  teamDisplayName: string;
  /** ドリルダウン対象日付（YYYY-MM-DD） */
  date: string;
  /** 集計期間種別 */
  periodType: '90d' | 'mtd';
  /** クリック元チャート種別 */
  chartType: 'bugRate' | 'releasePoints' | 'developPoints' | 'releaseCount';
}

/**
 * チームを明示指定してモーダルを開くときの引数。
 * KPI一覧テーブルのように行ごとにチームが違う呼び出し元が使う（画面上部のチームフィルタではなく
 * クリックされた行のチームでドリルダウンする必要があるため）。
 */
interface OpenForTeamInput {
  /** ドリルダウン対象チーム */
  team: KpiTeam;
  /** チーム表示名 */
  teamDisplayName: string;
  /** ドリルダウン対象日付（BigQuery DATE 形式のオブジェクトも受け付ける） */
  date: unknown;
  /** 集計期間種別（既定: 過去90日） */
  periodType?: '90d' | 'mtd';
  /** クリック元チャート種別（既定: バグ発生率） */
  chartType?: 'bugRate' | 'releasePoints' | 'developPoints' | 'releaseCount';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business KPIトレンドチャートのドリルダウンモーダルの開閉状態を管理するフック。点クリック時に resolveTeam でチーム情報を解決し、正規化した日付・期間種別・チャート種別とともにモーダルを開く。KPI一覧テーブルの行クリックなどチームが行ごとに決まる呼び出し元向けに、チームを明示指定して開く openModalForTeam も返す
 * @graph-connects extractDateString [calls] 日付文字列正規化
 */
export function useKpiDetailModal(resolveTeam: () => { team: KpiTeam; teamDisplayName: string }) {
  const [modal, setModal] = useState<KpiDetailModalState>({
    open: false,
    team: 'all',
    teamDisplayName: '',
    date: '',
    periodType: '90d',
    chartType: 'bugRate',
  });

  const openModalForTeam = ({
    team,
    teamDisplayName,
    date,
    periodType = '90d',
    chartType = 'bugRate',
  }: OpenForTeamInput) => {
    setModal({
      open: true,
      team,
      teamDisplayName,
      date: extractDateString(date),
      periodType,
      chartType,
    });
  };

  const openModal = (
    date: unknown,
    periodType: '90d' | 'mtd' = '90d',
    chartType: 'bugRate' | 'releasePoints' | 'developPoints' | 'releaseCount' = 'bugRate',
  ) => {
    const { team, teamDisplayName } = resolveTeam();
    openModalForTeam({ team, teamDisplayName, date, periodType, chartType });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, open: false }));
  };

  return { modal, openModal, openModalForTeam, closeModal };
}
